import { fetchCollectionCatalogItemById } from '@/redux/actions';
import { isTimerExpired } from '@/utils/isTimerExpired';
import { specificThemeRender } from '@/utils/specificThemeRender';
import { COLLECTION_CATALOG_ITEMS } from '@/utils/constants';
import { getThemeComponent } from '@/utils/themeImports';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import FlatList from '@/components/FlatList';
import VariantModal from '@/components/VariantModal';
import VariantChooseRepeatModal from '@/components/VariantChooseRepeatModal';
import VariantSelectedListModal from '@/components/VariantSelectedListModal';
import QuickViewModal from '@/components/DesignSystem/QuickViewModal';
import AdvanceCatalogMiddleBody from '@/components/DesignSystem/MiddleBody/MiddleBody7/AdvanceCatalogMiddleBody';
import useIsComponentMounted from '@/utils/useIsComponentMounted';
import { useSSRSelector } from '@/redux/ssrStore';
import LocalStorageHelper from '../../utils/LocalStorageHelper';
import CollectionCategory from './CollectionCategory';
import { ProductCustomizationPopUp } from '../../components/ProductCustomizationPopUp';
import { FullPageLoader } from '../../components/FullPageLoader/FullPageLoader';
import PromosDrawerAndButton from 'src/components/CouponsAndVouchers/PromosDrawerAndButton';
import PromoSlider from 'src/components/CouponsAndVouchers/PromoSlider';
import { PROMO_APPLICABILITY_TYPES } from 'src/constants/promos';
import { ProductListWrapper } from 'src/components/DesignSystem/Widgets2.0/ProductListWrapper';

const Collections = (props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const isComponentMounted = useIsComponentMounted();

  const collections = useSSRSelector((state) => state.catalogReducer.collections);
  const storeCollection = collections?.filter(
    (collection) => collection.id === +router.query.collectionId
  );

  const { collectionCatalogItems } = useSelector((state: any) => ({
    collectionCatalogItems: state.catalogReducer.collectionCatalogItems,
  }));

  const { storeData, personalizedProduct } = useSSRSelector((state) => ({
    storeData: state.storeReducer.store,
    personalizedProduct: state.catalogReducer?.personalizedProduct,
  }));

  /**
   * @return collectionCatalogItems from localstorage if it exists and within the cache clear time interval, else it will return from redux store.
   */
  const fetchCollectionCatalogItems = () => {
    const storedCollectionCatalogItems = LocalStorageHelper.get(COLLECTION_CATALOG_ITEMS);

    if (storedCollectionCatalogItems && !isTimerExpired()) {
      return JSON.parse(storedCollectionCatalogItems);
    }

    return collectionCatalogItems;
  };

  function isBodyAdvanceCatalog() {
    if (!router?.query?.collectionId) return false;
    if (storeData?.theme?.components?.PLP) {
      return storeData?.theme?.components?.PLP?.[0]?.name === 'SortingAndFilterWidget1';
    }
  }

  const returnNestedCollectionData = (collectionId, categoryId) => {
    const collectionCatalogItemList = fetchCollectionCatalogItems();
    const collectionIdValue = collectionCatalogItemList[collectionId];

    let categoryIdValue = undefined;
    if (collectionIdValue) {
      categoryIdValue = collectionIdValue[categoryId];
    }
    return categoryIdValue;
  };

  const loadMoreData = (category, collection) => {
    if (storeData?.store_id) {
      dispatch(
        fetchCollectionCatalogItemById({
          storeId: storeData.store_id,
          collectionId: collection.id,
          categoryId: category.id,
        })
      );
    }
  };

  function renderDefaultCollectionMiddleBody() {
    /** If the route contains a cid, that means we just want to display products corresponding to that category ID, hence return CollectionCategory component */
    if (router?.query?.cid) {
      return <CollectionCategory {...props} />;
    }

    return (
      <article className="collections-main">
        <section className="collections-body center">
          {storeCollection?.map((collection) => (
            <div key={collection.id}>
              <h2 data-id={collection.id} className="collection-heading tc">
                {collection.name}
              </h2>
              <PromoSlider
                type={PROMO_APPLICABILITY_TYPES.COLLECTION}
                typeId={collection.id}
              />
              {collection?.categories?.map((category) => (
                <div
                  key={category.id}
                  data-id={category.id}
                  className="category-items-section"
                  id={category.id}
                >
                  <h3 className="category-item-header">{category.name}</h3>
                  {/* <div className="category-item-body"> */}
                  <div className="md:pt-[20px] pb-[24px] tw-pt-[10px]">
                    <FlatList
                      isLoaderVisible={false}
                      isNext={
                        returnNestedCollectionData(collection.id, category.id)
                          ? false
                          : true
                      }
                      onScrollEnd={() => loadMoreData(category, collection)}
                      renderInsideFragment
                      renderList={() => (
                        <ProductListWrapper
                          productList={returnNestedCollectionData(
                            collection.id,
                            category.id
                          )}
                          oldCardConfigs={{
                            showButton: true,
                            noTags: false,
                          }}
                        />
                      )}
                    ></FlatList>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
      </article>
    );
  }

  return (
    <section className="collections-page">
      <FullPageLoader />
      {specificThemeRender(storeData)?.components?.Header?.map((layout) => {
        return getThemeComponent(layout.sub_type, layout.name, layout);
      })}
      {isBodyAdvanceCatalog()
        ? isComponentMounted && <AdvanceCatalogMiddleBody />
        : isComponentMounted && renderDefaultCollectionMiddleBody()}
      {specificThemeRender(storeData)?.components?.Footer?.map((layout) => {
        return getThemeComponent(layout.sub_type, layout.name, layout);
      })}
      <VariantModal />
      <VariantChooseRepeatModal />
      <VariantSelectedListModal />
      <QuickViewModal />
      {personalizedProduct?.personalizationPopUp && <ProductCustomizationPopUp />}
      <PromosDrawerAndButton />
    </section>
  );
};

export default Collections;
